import { FC, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { unwrapResult } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import dayjs, { Dayjs } from 'dayjs';

import { dateNormalizer, errorNormalizer } from '../../../shared/Helpers/functions';
import { CustomAlert, CustomButton, Picker } from '../../../shared/uiComponents';
import { EditSvg } from '../../../shared/Assets/svgIcons';
import { ButtonWrapper } from '../../../components/StyledComponents';
import { TimeInputsWrapper } from '../../Reports/helpers';
import { EventDataProperties } from '../../../redux/API/ClientAPIHelpers/calendarProperties';
import { UpdateSessionProperties } from '../../../redux/API/ClientAPIHelpers/sessionsProperties';
import { updateSession } from '../../../redux/State/clientSlice/sessionSlice';
import { getFlaggedSessions } from '../../../redux/State/clientSlice/dashboardSlice';
import { DispatchProperties } from '../../../redux/store';

interface EditSessionDateProps {
	row: EventDataProperties;
}

interface EditSessionDateContentProps {
	currentDate: string;
	endTime: string;

	onClose: () => void;
	sessionId: string;
	startTime: string;
}

const EditSessionDateContent: FC<EditSessionDateContentProps> = ({
	onClose,
	sessionId,
	currentDate,
	startTime,
	endTime,
}) => {
	const dispatch = useDispatch<DispatchProperties>();
	const [params] = useSearchParams();
	const [dateValue, setDateValue] = useState<Dayjs | null>(dayjs(currentDate));
	const [startTimeValue, setStartTimeValue] = useState<Dayjs | null>(dayjs(startTime));
	const [endTimeValue, setEndTimeValue] = useState<Dayjs | null>(dayjs(endTime));
	const [loading, setLoading] = useState<boolean>(false);

	const saveHandler = () => {
		if (!dateValue || !startTimeValue || !endTimeValue) return;

		setLoading(true);
		const newDate = dateNormalizer(dateValue);
		const newStartTime = `${newDate}T${startTimeValue.format('HH:mm')}`;
		const newEndTime = `${newDate}T${endTimeValue.format('HH:mm')}`;

		const updatedSession: UpdateSessionProperties = {
			id: sessionId,
			date: newDate,
			endTime: newEndTime,
			startTime: newStartTime,
		};

		dispatch(updateSession({ sessionId, data: updatedSession }))
			.then(unwrapResult)
			.then(() => {
				const page = params.get('sessionsPage') || '1';
				const pageSize = params.get('sessionsPageSize') || '8';
				dispatch(getFlaggedSessions({ page, pageSize }));
				toast('Session updated successfully');
				onClose();
			})
			.catch((error) => {
				errorNormalizer(error);
			})
			.finally(() => {
				setLoading(false);
			});
	};

	return (
		<div style={{ marginTop: '16px' }}>
			<Picker.CustomDate
				label="Date of session"
				onChange={setDateValue}
				value={dateValue}
				className="marginBottom16 paddingBottom16 paddingTop16"
			/>
			<TimeInputsWrapper>
				<Picker.CustomTime label="Start time" value={startTimeValue} onChange={setStartTimeValue} />
				<Picker.CustomTime label="End time" value={endTimeValue} onChange={setEndTimeValue} />
			</TimeInputsWrapper>
			<ButtonWrapper>
				<CustomButton
					title="Save"
					onClick={saveHandler}
					loading={loading}
					disabled={!dateValue || !startTimeValue || !endTimeValue || loading}
				/>
				<CustomButton title="Cancel" buttonType="secondaryGray" onClick={onClose} disabled={loading} />
			</ButtonWrapper>
		</div>
	);
};

const EditSessionDate: FC<EditSessionDateProps> = ({ row }) => {
	const { id, date, startTime, endTime } = row;
	const [openEditModal, setOpenEditModal] = useState<boolean>(false);

	const handleEditClick = () => {
		setOpenEditModal(true);
	};

	const handleCloseModal = () => {
		setOpenEditModal(false);
	};

	return (
		<>
			<div
				style={{
					cursor: 'pointer',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
				}}
				onClick={handleEditClick}
			>
				<EditSvg />
			</div>
			<CustomAlert
				open={openEditModal}
				onClose={handleCloseModal}
				title="Edit Session"
				Content={({ onClose }) => (
					<EditSessionDateContent
						onClose={onClose}
						sessionId={id}
						currentDate={date}
						startTime={startTime}
						endTime={endTime}
					/>
				)}
			/>
		</>
	);
};

export default EditSessionDate;
